import React, { JSX, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import PropTypes from "prop-types"
import { Breadcrumbs as MuiBreadcrumbs } from "@mui/material"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"

import { useLoading } from "../../LoadingContext"
import { getFacility } from "../../../api/facility"
import { getMedicalRecord } from "../../../api/medical_record"

interface Props {
  icon: string
  title: string
  route: string | string[]
  light?: boolean
}

export default function Breadcrumbs({ icon, title, route, light }: Props): JSX.Element {
  const { startLoading, stopLoading } = useLoading()
  const routes = Array.isArray(route) ? route.slice(0, -1) : [route]
  const [facility, setFacility] = useState<{ name: string; link: string }>({
    name: "",
    link: ""
  })

  const [recode, setRecode] = useState<{ name: string; link: string }>({
    name: "",
    link: ""
  })

  const getData = async () => {
    try {
      startLoading()
      switch (routes[0]) {
        case "facility": {
          // 施設詳細ページの処理
          const facilityData = await getFacility(title)
          setFacility({ name: facilityData.name, link: `/facility/${facilityData.facility_id}` })
          break
        }

        case "tenant": {
          // テナントページの処理
          const facilityData = await getFacility(routes[1])
          setFacility({ name: facilityData.name, link: `/facility/${facilityData.facility_id}` })

          const medicalRecord = await getMedicalRecord(title)
          if (!medicalRecord || !medicalRecord.name || !medicalRecord.medical_record_id) {
            break
          }
          setRecode({
            name: medicalRecord.name,
            link: `/tenant/${facilityData.facility_id}/${medicalRecord.medical_record_id.split("|")[1]}`
          })
          break
        }

        default:
          switch (title) {
            case "home":
              setFacility({ name: "ホーム", link: "/home" })
              break
            case "data":
              // データ登録ページの処理
              setFacility({ name: "データ登録", link: "/data" })
              break

            case "manage_account":
              // アカウント管理ページの処理
              setFacility({ name: "アカウント", link: "/manage_account" })
              break

            case "manage_catv":
              // サービス提供元管理ページの処理
              setFacility({ name: "サービス提供元", link: "/manage_catv" })
              break

            case "manage_estate":
              // 不動産業者管理ページの処理
              setFacility({ name: "不動産業者", link: "/manage_estate" })
              break

            case "manage_facility":
              // 施設管理ページの処理
              setFacility({ name: "施設管理", link: "/manage_facility" })
              break

            case "manage_device":
              // デバイス管理ページの処理
              setFacility({ name: "デバイス", link: "/manage_device" })
              break
            case "life-log":
              // ライフログページの処理
              setFacility({ name: "ライフログ", link: "/life-log" })
              break
            case "manage":
              // 管理ページの処理
              setFacility({ name: "管理", link: "/manage" })
              break
            case "manage_estate_mail":
              // 不動産業者のメール送信先設定
              setFacility({ name: "検知メール送信先", link: "/manage_estate_mail" })
              break
            case "manage_detect_setting":
              // 不動産業者のメール送信先設定
              setFacility({ name: "検知設定", link: "/manage_detect_setting" })
              break
            case "manage_sensitivity_setting":
              // 不動産業者のメール送信先設定
              setFacility({ name: "感度設定", link: "/manage_sensitivity_setting" })
              break
            case "data_import":
              setFacility({ name: "初期データ登録", link: "/data_import" })
              break
            case "help":
              // ヘルプページの処理（未実装）
              setFacility({ name: "ヘルプ(未実装)", link: "/help" })
              break
          }
          break
      }
    } catch (error: any) {
      console.log(error)
    } finally {
      stopLoading()
    }
  }

  useEffect(() => {
    getData()
  }, [title])

  return (
    <MDBox mr={{ xs: 0, xl: 8 }}>
      <MuiBreadcrumbs
        sx={{
          "& .MuiBreadcrumbs-separator": {
            color: ({ palette: { white, grey } }) => (light ? white.main : grey[600]),
            mx: 0.2,
            lineHeight: "1.2rem"
          },
          "& .MuiBreadcrumbs-li": {
            mx: 0.2,
            lineHeight: "1.2rem"
          }
        }}
      >
        <Link to="/home">
          <MDTypography
            component="span"
            variant="body2"
            color={light ? "white" : "dark"}
            opacity={light ? 0.8 : 0.5}
            sx={{ lineHeight: 0 }}
          >
            <Icon>{icon}</Icon>
          </MDTypography>
        </Link>
        {facility.link !== "/home" ? (
          <Link to={`${facility.link}`}>
            <MDTypography
              key={facility.link}
              component="span"
              variant="button"
              fontWeight="regular"
              textTransform="capitalize"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{
                lineHeight: "normal",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "100px",
                display: "inline-block"
              }}
              mt={0.4}
            >
              {facility.name}
            </MDTypography>
          </Link>
        ) : null}
        {recode.link !== "" ? (
          <Link to={recode.link}>
            <MDTypography
              key={recode.link}
              component="span"
              variant="button"
              fontWeight="regular"
              textTransform="capitalize"
              color={light ? "white" : "dark"}
              opacity={light ? 0.8 : 0.5}
              sx={{ lineHeight: 0 }}
            >
              {recode.name}
            </MDTypography>
          </Link>
        ) : null}
      </MuiBreadcrumbs>
      {/* <MDTypography
        fontWeight="bold"
        textTransform="capitalize"
        variant="h6"
        color={light ? "white" : "dark"}
        noWrap
      >
        {/* {title.replace("-", " ")} }
      </MDTypography> */}
    </MDBox>
  )
}

// Typechecking props for the Breadcrumbs
Breadcrumbs.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  route: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  light: PropTypes.bool
}

// Declaring default props for the Breadcrumbs
Breadcrumbs.defaultProps = {
  light: false
}
